import { ArrowLeft, Plus, Star, TableProperties, Upload } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useNavigate } from "react-router-dom";

export function SchemaCreatePage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-[calc(100vh-72px)] animate-fade-in">
      <section className="px-12 py-10">
        <button
          type="button"
          onClick={() => navigate("/schema-builder")}
          className="mb-8 flex items-center gap-2 text-sm font-medium text-[var(--color-text-primary)] transition hover:text-[var(--color-brand-primary)]"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>

        <h1 className="mb-3 text-4xl font-semibold tracking-tight text-[var(--color-text-primary)]">
          Create New Schema
        </h1>

        <p className="mb-8 text-2xl font-light text-[var(--color-text-secondary)]">
          How would you like to start?
        </p>

        <div className="flex max-w-[720px] flex-col gap-5">
          <CreateOptionCard
            title="Build from File"
            description="Upload a sample file from which Intelligent Data Intake can parse column names and data formats, then configure your own rules"
            actionIcon={Upload}
            onClick={() => navigate("/schema-builder/create/file")}
          />

          <CreateOptionCard
            title="Build from Template"
            description="Choose from industry-standard schemas. Use them as-is or make tweaks to suit your organization’s needs"
            actionIcon={Star}
            onClick={() => navigate("/schema-builder/create/template")}
          />

          <CreateOptionCard
            title="Start from Scratch"
            description="Create your schema from a blank canvas -- the world is your oyster."
            actionIcon={Plus}
            onClick={() => navigate("/schema-builder/create/scratch")}
          />
        </div>
      </section>
    </div>
  );
}

interface CreateOptionCardProps {
  title: string;
  description: string;
  actionIcon: LucideIcon;
  onClick: () => void;
}

function CreateOptionCard({
  title,
  description,
  actionIcon: ActionIcon,
  onClick,
}: CreateOptionCardProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={title}
      className="flex min-h-[112px] items-center gap-8 rounded-2xl border border-[var(--color-border-light)] bg-white px-8 py-6 text-left shadow-md transition hover:-translate-y-0.5 hover:shadow-lg"
    >
      <div className="relative h-14 w-14 shrink-0">
        <TableProperties
          className="h-full w-full text-[var(--color-action-primary)]"
          strokeWidth={1}
          style={{ transform: "scaleX(-1) scale(1.25)" }}
        />

        <div className="absolute -bottom-3 -right-3 rounded-lg bg-white p-0.5">
          <ActionIcon className="h-6 w-6 text-[var(--color-action-primary)]" strokeWidth={2.5} />
        </div>
      </div>

      <div>
        <h2 className="mb-2 text-lg font-semibold text-[var(--color-text-primary)]">{title}</h2>

        <p className="text-sm leading-6 text-[var(--color-text-secondary)]">{description}</p>
      </div>
    </button>
  );
}
